import type { SimulationLinkDatum, SimulationNodeDatum } from 'd3-force';
import { forceCenter, forceCollide, forceLink, forceManyBody, forceSimulation } from 'd3-force';
import { useEffect, useMemo, useRef, useState } from 'react';

import type { GraphNode, WikiGraph } from '@/api/client';
import { buildPath, defaultLang } from '@/app/routes';
import { MAP } from '@/config';
import { useT } from '@/i18n/LocaleProvider';
import { CategoryFilter } from './CategoryFilter';
import { categoryColor, extractCategories, isNodeActive } from './colors';
import { hitTest } from './hitTest';

export type SiteMapCanvasProps = {
  graph: WikiGraph;
  lang?: string;
  onNavigate?: (slug: string) => void;
};

type SimNode = SimulationNodeDatum & GraphNode & { radius: number };
type SimLink = SimulationLinkDatum<SimNode>;

function isMissingNode(node: GraphNode): boolean {
  return node.missing || node.stub;
}

function nodeRadius(node: GraphNode): number {
  const linkCount = node.inbound_count + node.outbound_count;
  return Math.max(
    MAP.minRadius,
    Math.min(MAP.maxRadius, MAP.minRadius + linkCount * MAP.radiusScale),
  );
}

export function SiteMapCanvas({ graph, lang = defaultLang, onNavigate }: SiteMapCanvasProps) {
  const t = useT();
  const canvasRef = useRef<HTMLCanvasElement>(null);
  // Keep latest onNavigate without restarting the effect
  const onNavigateRef = useRef(onNavigate);
  onNavigateRef.current = onNavigate;

  // Category filter state — null means "show all"
  const allCategories = useMemo(() => extractCategories(graph.nodes), [graph]);
  const [activeCategories, setActiveCategories] = useState<Set<string> | null>(null);

  // Reset filter when graph identity changes (new fetch)
  // biome-ignore lint/correctness/useExhaustiveDependencies: graph prop identity is the intended signal
  useEffect(() => {
    setActiveCategories(null);
  }, [graph]);

  // Ref so draw() reads the latest filter without restarting the simulation
  const activeCategoriesRef = useRef<Set<string> | null>(null);
  activeCategoriesRef.current = activeCategories;

  // draw function stored in a ref so the filter effect can call it after the simulation settles
  const drawRef = useRef<(() => void) | null>(null);

  // Redraw when filter changes without restarting the simulation (ref pattern)
  // biome-ignore lint/correctness/useExhaustiveDependencies: activeCategories change is the intended trigger; draw reads it via ref
  useEffect(() => {
    drawRef.current?.();
  }, [activeCategories]);

  useEffect(() => {
    if (!canvasRef.current) return;
    // Capture after null-check so TypeScript infers HTMLCanvasElement in closures
    const canvas = canvasRef.current;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const rootStyle = getComputedStyle(document.documentElement);
    const ink = rootStyle.getPropertyValue('--ink').trim() || MAP.fallbackColors.ink;
    const muted = rootStyle.getPropertyValue('--muted').trim() || MAP.fallbackColors.muted;
    const crimson = rootStyle.getPropertyValue('--crimson').trim() || MAP.fallbackColors.crimson;

    const width = canvas.clientWidth || MAP.defaultWidth;
    const height = canvas.clientHeight || MAP.defaultHeight;
    canvas.width = width;
    canvas.height = height;

    const nodes: SimNode[] = graph.nodes.map((node) => ({
      ...node,
      radius: nodeRadius(node),
    }));

    const nodeById = new Map<string, SimNode>(nodes.map((n) => [n.id, n]));

    const links: SimLink[] = graph.edges
      .filter((e) => nodeById.has(e.from) && nodeById.has(e.to))
      .map((e) => ({ source: e.from, target: e.to }));

    const transform = { x: 0, y: 0, k: 1 };

    function draw() {
      if (!ctx) return;

      const cats = activeCategoriesRef.current;

      ctx.clearRect(0, 0, width, height);
      ctx.save();
      ctx.translate(transform.x, transform.y);
      ctx.scale(transform.k, transform.k);

      // Draw inactive edges (dim)
      ctx.beginPath();
      ctx.strokeStyle = muted;
      ctx.lineWidth = 1;
      ctx.globalAlpha = 0.08;
      for (const link of links) {
        const s = link.source;
        const t = link.target;
        if (typeof s !== 'object' || s === null || typeof t !== 'object' || t === null) continue;
        if (s.x == null || s.y == null || t.x == null || t.y == null) continue;
        if (isNodeActive(s, cats) && isNodeActive(t, cats)) continue;
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(t.x, t.y);
      }
      ctx.stroke();

      // Draw active edges
      ctx.beginPath();
      ctx.globalAlpha = 0.4;
      for (const link of links) {
        const s = link.source;
        const t = link.target;
        if (typeof s !== 'object' || s === null || typeof t !== 'object' || t === null) continue;
        if (s.x == null || s.y == null || t.x == null || t.y == null) continue;
        if (!isNodeActive(s, cats) || !isNodeActive(t, cats)) continue;
        ctx.moveTo(s.x, s.y);
        ctx.lineTo(t.x, t.y);
      }
      ctx.stroke();

      // Draw nodes
      for (const node of nodes) {
        if (node.x == null || node.y == null) continue;
        const r = node.radius;
        const missing = isMissingNode(node);
        const active = isNodeActive(node, cats);

        ctx.globalAlpha = active ? 1 : 0.15;
        ctx.beginPath();
        ctx.arc(node.x, node.y, r, 0, Math.PI * 2);

        if (missing) {
          ctx.setLineDash([3, 3]);
          ctx.strokeStyle = muted;
          ctx.lineWidth = 1.5;
          ctx.stroke();
          ctx.setLineDash([]);
        } else {
          ctx.fillStyle = categoryColor(node.categories?.[0], MAP.palette) ?? crimson;
          ctx.fill();
        }

        if (active && r >= MAP.labelMinRadius) {
          ctx.font = MAP.labelFont;
          ctx.fillStyle = ink;
          ctx.fillText(node.title, node.x + r + 3, node.y + 4);
        }
      }

      ctx.globalAlpha = 1;
      ctx.restore();
    }

    drawRef.current = draw;

    function canvasPoint(e: MouseEvent): { x: number; y: number } {
      const rect = canvas.getBoundingClientRect();
      return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    }

    const drag = {
      active: false,
      kind: 'pan' as 'pan' | 'node',
      startX: 0,
      startY: 0,
      startTX: 0,
      startTY: 0,
      node: null as SimNode | null,
      moved: false,
    };

    const simulation = forceSimulation<SimNode>(nodes)
      .force(
        'link',
        forceLink<SimNode, SimLink>(links)
          .id((d) => d.id)
          .distance(MAP.linkDistance),
      )
      .force('charge', forceManyBody<SimNode>().strength(MAP.charge))
      .force('center', forceCenter<SimNode>(width / 2, height / 2))
      .force(
        'collide',
        forceCollide<SimNode>((d) => d.radius + MAP.collideGap),
      )
      .alphaDecay(MAP.alphaDecay)
      .on('tick', draw);

    function handleWheel(e: WheelEvent) {
      e.preventDefault();
      const pt = canvasPoint(e);
      const factor = e.deltaY < 0 ? 1.1 : 1 / 1.1;
      const newK = Math.max(MAP.zoomMin, Math.min(MAP.zoomMax, transform.k * factor));
      // Zoom toward the cursor: adjust translate so the world point under the
      // cursor stays fixed in screen space after scaling.
      transform.x = pt.x - (pt.x - transform.x) * (newK / transform.k);
      transform.y = pt.y - (pt.y - transform.y) * (newK / transform.k);
      transform.k = newK;
      draw();
    }

    function handleMouseDown(e: MouseEvent) {
      if (e.button !== 0) return;
      const pt = canvasPoint(e);
      const hitId = hitTest(nodes, pt, transform);

      drag.active = true;
      drag.startX = pt.x;
      drag.startY = pt.y;
      drag.moved = false;

      if (hitId != null) {
        const node = nodeById.get(hitId);
        if (node != null) {
          drag.kind = 'node';
          drag.node = node;
          node.fx = node.x;
          node.fy = node.y;
          return;
        }
      }

      drag.kind = 'pan';
      drag.startTX = transform.x;
      drag.startTY = transform.y;
      drag.node = null;
    }

    function handleMouseMove(e: MouseEvent) {
      const pt = canvasPoint(e);

      if (drag.active && drag.kind === 'pan') {
        transform.x = drag.startTX + (pt.x - drag.startX);
        transform.y = drag.startTY + (pt.y - drag.startY);
        draw();
        canvas.style.cursor = 'grabbing';
        return;
      }

      if (drag.active && drag.kind === 'node' && drag.node != null) {
        const dx = pt.x - drag.startX;
        const dy = pt.y - drag.startY;
        if (Math.abs(dx) > 2 || Math.abs(dy) > 2) drag.moved = true;
        drag.node.fx = (pt.x - transform.x) / transform.k;
        drag.node.fy = (pt.y - transform.y) / transform.k;
        simulation.alphaTarget(0.1).restart();
        canvas.style.cursor = 'grabbing';
        return;
      }

      // Hover cursor feedback
      const hitId = hitTest(nodes, pt, transform);
      if (hitId != null) {
        const node = nodeById.get(hitId);
        canvas.style.cursor =
          node != null && !isMissingNode(node) && node.slug != null ? 'pointer' : 'default';
      } else {
        canvas.style.cursor = 'grab';
      }
    }

    function handleMouseUp(e: MouseEvent) {
      if (e.button !== 0 || !drag.active) return;

      if (drag.kind === 'node' && drag.node != null) {
        if (!drag.moved && !isMissingNode(drag.node) && drag.node.slug != null) {
          onNavigateRef.current?.(drag.node.slug);
        }
        drag.node.fx = null;
        drag.node.fy = null;
        simulation.alphaTarget(0);
      }

      drag.active = false;
      drag.node = null;
      canvas.style.cursor = 'grab';
    }

    canvas.style.cursor = 'grab';
    canvas.addEventListener('wheel', handleWheel, { passive: false });
    canvas.addEventListener('mousedown', handleMouseDown);
    canvas.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    return () => {
      drawRef.current = null;
      simulation.stop();
      canvas.removeEventListener('wheel', handleWheel);
      canvas.removeEventListener('mousedown', handleMouseDown);
      canvas.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [graph]);

  // Type predicate so slug is typed as string (not string | undefined) in the map
  const navigableNodes = graph.nodes.filter(
    (n): n is typeof n & { slug: string } => !isMissingNode(n) && n.slug != null,
  );

  return (
    <>
      <CategoryFilter
        categories={allCategories}
        active={activeCategories}
        onChange={setActiveCategories}
      />
      <div className="site-map-canvas-container">
        <canvas
          ref={canvasRef}
          className="site-map-canvas"
          data-testid="site-map-canvas"
          aria-label={t('map_title')}
        />
        <ul className="site-map-a11y-links" aria-label={t('map_canvas_links_label')}>
          {navigableNodes.map((node) => (
            <li key={node.id}>
              <a href={buildPath({ kind: 'reader', slug: node.slug, lang })}>{node.title}</a>
            </li>
          ))}
        </ul>
      </div>
    </>
  );
}
